import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { X, Check, ArrowUpRight, Bell, Target, Clock, LineChart, Quote, Volume2, VolumeX, Play } from 'lucide-react';
import BlurText from './BlurText';
import { useCountryLink } from '../context/CountryContext';
import { realityCheck, stats, testimonials } from '../mock';

const SERIF = { fontFamily: "'Nacelle', sans-serif", fontWeight: 600 };
const SANS = { fontFamily: "'Nacelle', sans-serif", fontWeight: 400 };

const miniFeatures = [
  { icon: Bell, title: 'Real Time Alerts', desc: 'Telegram pings the moment a valid zone forms.' },
  { icon: Target, title: 'Pair Included', desc: 'Know exactly which pair is being triggered.' },
  { icon: Clock, title: 'Timeframe + Entry', desc: 'M15, M30, H1 with entry price at a glance.' },
  { icon: LineChart, title: 'Built for Busy Traders', desc: 'No screens needed. Live your day, alert pings you.' },
];

const fadeUp = (delay = 0) => ({
  initial: { filter: 'blur(10px)', opacity: 0, y: 20 },
  whileInView: { filter: 'blur(0px)', opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.2 },
  transition: { duration: 0.8, ease: 'easeOut', delay },
});

const TransformSection = () => {
  const { link } = useCountryLink();
  const headRef = useRef(null);
  const inView = useInView(headRef, { once: true, amount: 0.3 });
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (videoRef.current.paused) {
      videoRef.current.play();
      setPlaying(true);
    } else {
      videoRef.current.pause();
      setPlaying(false);
    }
  };

  return (
    <section
      id="system"
      className="relative bg-black py-20 sm:py-24 lg:py-36 overflow-hidden"
    >
      {/* Cinematic amber glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 50% 0%, rgba(255,110,0,0.10) 0%, rgba(255,110,0,0) 50%)',
        }}
      />
      <div className="relative max-w-6xl mx-auto px-5 lg:px-8">
        {/* Heading */}
        <div className="text-center max-w-4xl mx-auto" ref={headRef}>
          <motion.div
            {...fadeUp(0.1)}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-[#FF8533]/25 bg-[#FF8533]/[0.05] text-[#FFA66B] text-[11px] tracking-[0.2em] font-medium uppercase"
            style={SANS}
          >
            The Transformation
          </motion.div>
          <div className="mt-7" style={SERIF}>
            {inView && (
              <BlurText
                text="You Don’t Need More Chart Hours. You Need Better Timing."
                className="text-4xl sm:text-5xl md:text-6xl lg:text-[4.75rem] text-white leading-[0.9] tracking-[-2px]"
              />
            )}
          </div>
          <motion.p
            {...fadeUp(0.6)}
            className="mt-7 text-white/65 text-base md:text-[17px] leading-relaxed max-w-2xl mx-auto font-light"
            style={SANS}
          >
            MBE Alert pings your Telegram the moment a valid bubble zone forms.
            Pair, timeframe, entry, already structured. You decide and execute.
          </motion.p>
        </div>

        {/* Cinematic Vertical Video Showcase */}
        <motion.div
          id="video-demo"
          {...fadeUp(0.2)}
          className="mt-14 sm:mt-16 relative mx-auto max-w-sm sm:max-w-md scroll-mt-24"
        >
          {/* Orange glow halo */}
          <div className="absolute -inset-6 sm:-inset-10 bg-gradient-to-br from-[#FF6E00]/30 via-[#FF8533]/12 to-transparent rounded-[2rem] blur-3xl pointer-events-none" />

          <div className="relative rounded-[1.5rem] sm:rounded-[1.75rem] overflow-hidden border border-[#FF8533]/25 bg-black shadow-[0_40px_120px_-30px_rgba(255,110,0,0.45)]">
            {/* Live label */}
            <div className="absolute top-3 left-3 sm:top-4 sm:left-4 z-10 flex items-center gap-2">
              <span
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-black/60 backdrop-blur-md border border-white/15 text-white text-[10.5px] sm:text-[11px] font-medium"
                style={SANS}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
                Live demo
              </span>
            </div>

            {/* Controls */}
            <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10 flex items-center gap-1.5 sm:gap-2">
              <button
                type="button"
                onClick={togglePlay}
                aria-label={playing ? 'Pause video' : 'Play video'}
                className="w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-md border border-white/15 text-white flex items-center justify-center transition-colors"
              >
                <Play className={`w-3.5 h-3.5 ${playing ? 'opacity-40' : 'fill-white'}`} />
              </button>
              <button
                type="button"
                onClick={toggleMute}
                aria-label={muted ? 'Unmute video' : 'Mute video'}
                className="w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-md border border-white/15 text-white flex items-center justify-center transition-colors"
              >
                {muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
              </button>
            </div>

            <video
              ref={videoRef}
              src="https://customer-assets.emergentagent.com/job_theme-17/artifacts/nheb5mmd_MBE%20ALERTS%20CTA%2001%20%281%29.mp4"
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              aria-label="MBE Alert demo showing real-time Telegram notification"
              className="w-full h-full object-cover block aspect-[9/16] bg-black"
            />

            {/* Caption bottom */}
            <div className="absolute left-0 right-0 bottom-0 px-4 sm:px-5 py-4 sm:py-5 bg-gradient-to-t from-black/90 via-black/55 to-transparent">
              <div
                className="text-white text-[20px] sm:text-[24px] tracking-[-0.5px] leading-tight"
                style={SERIF}
              >
                Watch how a valid zone becomes a clean entry.
              </div>
              <div
                className="mt-1 text-white/55 text-[11.5px] sm:text-[12.5px] font-light"
                style={SANS}
              >
                Real alert. Real pair. Real timing.
              </div>
            </div>
          </div>

          {/* CTA below the video */}
          <div className="mt-6 flex justify-center">
            <a
              href={link} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#FF6E00] hover:bg-[#FF8533] text-white font-medium text-[14px] sm:text-[15px] px-5 sm:px-6 py-3 sm:py-3.5 rounded-full transition-colors shadow-[0_18px_40px_-15px_rgba(255,110,0,0.75)]"
              style={SANS}
            >
              Get MBE Alert Now <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          {/* Convincer microcopy */}
          <div
            className="mt-6 flex flex-wrap items-center justify-center gap-x-5 sm:gap-x-7 gap-y-2 text-white/60 text-[12px] sm:text-[12.5px] font-light"
            style={SANS}
          >
            <span className="inline-flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 text-[#FF8533]" /> Fires before candle closes
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 text-[#FF8533]" /> Pair · TF · Entry structured
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Check className="w-3.5 h-3.5 text-[#FF8533]" /> Zero noise
            </span>
          </div>
        </motion.div>

        {/* Old vs New */}
        <div className="mt-16 grid md:grid-cols-2 gap-5">
          <motion.div
            {...fadeUp(0.1)}
            className="rounded-2xl p-8 bg-white/[0.02] border border-white/8"
          >
            <div
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-rose-400/20 bg-rose-500/[0.06] text-rose-300/90 text-[10.5px] tracking-[0.2em] font-medium uppercase"
              style={SANS}
            >
              The Old Way
            </div>
            <h3
              className="mt-5 text-3xl sm:text-4xl text-white/85 leading-[1] tracking-[-1px]"
              style={SERIF}
            >
              Watching candles, missing zones.
            </h3>
            <ul className="mt-6 space-y-3" style={SANS}>
              {realityCheck.old.map((t, i) => (
                <li key={i} className="flex items-start gap-3">
                  <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full bg-rose-500/10 border border-rose-500/20 flex items-center justify-center">
                    <X className="w-3.5 h-3.5 text-rose-300" />
                  </span>
                  <span className="text-white/60 text-[14.5px] font-light">{t}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            {...fadeUp(0.2)}
            className="rounded-2xl p-8 bg-gradient-to-br from-[#FF6E00]/[0.08] to-[#FF8533]/[0.02] border border-[#FF8533]/25 relative overflow-hidden"
          >
            <div className="absolute -top-10 -right-10 w-44 h-44 rounded-full bg-[#FF8533]/15 blur-3xl pointer-events-none" />
            <div className="relative">
              <div
                className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#FF8533]/30 bg-[#FF6E00]/[0.1] text-[#FFA66B] text-[10.5px] tracking-[0.2em] font-medium uppercase"
                style={SANS}
              >
                The MBE Alert Way
              </div>
              <h3
                className="mt-5 text-3xl sm:text-4xl text-white leading-[1] tracking-[-1px]"
                style={SERIF}
              >
                Live your day. Catch every zone.
              </h3>
              <ul className="mt-6 space-y-3" style={SANS}>
                {realityCheck.new.map((t, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="shrink-0 mt-0.5 w-6 h-6 rounded-full bg-[#FF6E00]/20 border border-[#FF8533]/40 flex items-center justify-center">
                      <Check className="w-3.5 h-3.5 text-[#FFA66B]" />
                    </span>
                    <span className="text-white/85 text-[14.5px] font-light">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>

        {/* Key features + Hand mockup */}
        <div className="mt-20 text-center">
          <motion.h3
            {...fadeUp(0)}
            className="text-3xl sm:text-4xl md:text-5xl text-white tracking-[-1.5px] leading-[1]"
            style={SERIF}
          >
            Everything you need. <span className="text-[#FF8533]">Nothing you don’t.</span>
          </motion.h3>
          <motion.p
            {...fadeUp(0.15)}
            className="mt-4 text-white/55 text-[15px] max-w-xl mx-auto font-light"
            style={SANS}
          >
            Four mechanics. Zero noise. Built around the MBE system.
          </motion.p>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-8 lg:gap-10 items-center">
          {/* Hand Mockup Image */}
          <motion.div
            {...fadeUp(0.1)}
            className="lg:col-span-5 relative order-1"
          >
            {/* Orange halo glow */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#FF6E00]/25 via-[#FF8533]/12 to-transparent blur-3xl rounded-full pointer-events-none" />
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_theme-17/artifacts/gy1430cm_Gemini_Generated_Image_76x15q76x15q76x1.png"
                alt="MBE Alert delivered to your phone via Telegram"
                className="w-full max-w-md mx-auto block"
                width="900"
                height="900"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="relative mt-4 text-center">
              <div
                className="text-white text-[20px] sm:text-[24px] tracking-[-0.5px] leading-tight"
                style={SERIF}
              >
                Real alerts. Right on your phone.
              </div>
              <p
                className="mt-2 text-white/55 text-[13px] sm:text-[13.5px] max-w-sm mx-auto"
                style={SANS}
              >
                Pair, price, timeframe, SL, TP — all delivered structured to your
                Telegram, the moment a zone is valid.
              </p>
            </div>
          </motion.div>

          {/* Feature cards 2x2 */}
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4 order-2">
            {miniFeatures.map((f, i) => (
              <motion.div
                key={i}
                {...fadeUp(i * 0.08)}
                className="group bg-white/[0.025] border border-white/8 rounded-2xl p-6 hover:border-[#FF8533]/30 hover:bg-white/[0.045] transition-all duration-300"
              >
                <div className="w-10 h-10 rounded-xl bg-[#FF6E00]/15 border border-[#FF8533]/25 flex items-center justify-center mb-5 group-hover:bg-[#FF6E00]/25 transition-colors">
                  <f.icon className="w-4.5 h-4.5 text-[#FFA66B]" />
                </div>
                <h4
                  className="text-white text-2xl tracking-[-0.5px] leading-tight"
                  style={SERIF}
                >
                  {f.title}
              </h4>
              <p
                className="mt-2 text-white/55 text-[13.5px] leading-relaxed font-light"
                style={SANS}
              >
                {f.desc}
              </p>
            </motion.div>
          ))}
        </div>
        </div>

        {/* Stats + testimonials */}
        <motion.div
          {...fadeUp(0.1)}
          className="mt-20 rounded-3xl bg-gradient-to-br from-[#0c1428] to-[#070b15] border border-white/8 p-10 sm:p-12"
        >
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((s, i) => (
              <div key={i} className="text-center">
                <div
                  className="text-[#FF8533] text-[44px] sm:text-[54px] tracking-[-2px] leading-none"
                  style={SERIF}
                >
                  {s.value}
                </div>
                <div
                  className="mt-2 text-white/55 text-[11.5px] uppercase tracking-[0.18em] font-light"
                  style={SANS}
                >
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 pt-10 border-t border-white/8 grid md:grid-cols-3 gap-6">
            {testimonials.slice(0, 3).map((t, i) => (
              <div key={i} className="flex flex-col">
                <Quote className="w-5 h-5 text-[#FF8533]/80" />
                <p
                  className="mt-4 text-white/80 text-[15px] leading-relaxed"
                  style={SERIF}
                >
                  “{t.quote}”
                </p>
                <div className="mt-4 pt-3 border-t border-white/5">
                  <div className="text-white text-[13px] font-medium" style={SANS}>
                    {t.name}
                  </div>
                  <div className="text-white/45 text-[11.5px] mt-0.5 font-light" style={SANS}>
                    {t.role}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div {...fadeUp(0.1)} className="mt-14 text-center">
          <a
            href={link} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#FF6E00] hover:bg-[#FF8533] text-black font-medium text-[15px] px-6 py-3.5 rounded-full transition-colors shadow-[0_18px_40px_-15px_rgba(255,110,0,0.7)]"
            style={SANS}
          >
            Get MBE Alert Now <ArrowUpRight className="w-4 h-4" />
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default TransformSection;
